import { useCallback, useMemo } from 'react';
import { makeStartIterationInstruction, makeStopIterationInstruction } from '@gonfalon/experiments/instructions';
import type { Experiment } from '@gonfalon/experiments/types';
import { msg } from '@gonfalon/messages/msg';
import { getQueryClient } from '@gonfalon/react-query-client/getQueryClient';
import { useUpdateExperiment } from '@gonfalon/rest-api/hooks/useUpdateExperiment';
import { holdoutsDetailRoot } from '@gonfalon/rest-api/queries/holdouts';
import { toastQueue } from '@launchpad-ui/components';

import { invalidateExperimentQueries } from '#components/experimentation/common/utils/invalidateExperimentQueries';
import { snackbarAssignmentSourceProbe } from '#components/experimentation/experiments/draft-experiment/ExperimentDesignHealthCheck/snackbarAssignmentSourceProbe';
import { trackIterationStarted } from '#utils/analyticsUtils';

export const useUpdateExperimentStatusCallbacks = ({
  experiment,
  projectKey,
  environmentKey,
}: {
  experiment: Experiment | undefined;
  projectKey: string;
  environmentKey: string;
}) => {
  const { mutateAsync: updateExperimentMutationAsync, isPending: isUpdateExperimentPending } = useUpdateExperiment({
    shouldInvalidateQueries: false,
  });

  if (!experiment) {
    throw new Error('Experiment not found');
  }

  const isMAB = experiment?.type === 'mab';
  const isHoldout = experiment?.type === 'holdout';

  const startExperimentIteration = useCallback(
    async (changeJustification?: string, forceCreateIteration?: boolean, onStartSuccess?: () => void) => {
      const previousIterationStatus = experiment.currentIteration?.status;
      try {
        const updatedExperiment = await updateExperimentMutationAsync(
          {
            projectKey,
            environmentKey,
            experimentKey: experiment.key,
            body: {
              instructions: [makeStartIterationInstruction(changeJustification)],
            },
          },
          {
            onSuccess: async () => {
              await invalidateExperimentQueries({
                projectKey,
                environmentKey,
                experimentKey: experiment.key,
              });

              onStartSuccess?.();
            },
          },
        );
        snackbarAssignmentSourceProbe(updatedExperiment?.assignmentSourceProbe);

        if (!isHoldout) {
          trackIterationStarted({
            experimentKey: experiment.key,
            projectKey,
            environmentKey,
            experimentType: experiment.type,
            source: previousIterationStatus === 'not_started' ? 'first_start' : 'restart',
            iteration: updatedExperiment?.currentIteration,
          });
        }

        if (!forceCreateIteration) {
          await invalidateExperimentQueries({
            projectKey,
            environmentKey,
            experimentKey: experiment.key,
          });
          if (isHoldout) {
            const queryClient = getQueryClient();
            await queryClient.invalidateQueries({
              queryKey: holdoutsDetailRoot,
            });
          }
        }
      } catch (error: unknown) {
        toastQueue.add({
          title: msg('experiments.experimentIterationToast.startFailed', { isMab: isMAB ? 'true' : 'false' }),
          status: 'error',
          description:
            error instanceof Error ? error.message : msg('experiments.experimentIterationToast.unknownError'),
        });
        throw error;
      }
    },
    [updateExperimentMutationAsync, projectKey, environmentKey, experiment, isMAB, isHoldout],
  );

  const stopExperimentIteration = useCallback(
    async (winningTreatmentId: string, winningReason: string) => {
      try {
        await updateExperimentMutationAsync(
          {
            projectKey,
            environmentKey,
            experimentKey: experiment.key,
            body: {
              instructions: [makeStopIterationInstruction(winningTreatmentId, winningReason)],
            },
          },
          {
            onSuccess: async () => {
              await invalidateExperimentQueries({
                projectKey,
                environmentKey,
                experimentKey: experiment.key,
              });
            },
          },
        );
        toastQueue.add({
          title: msg('experiments.experimentIterationToast.stopped', { isMab: isMAB ? 'true' : 'false' }),
          status: 'success',
        });
      } catch (error: unknown) {
        toastQueue.add({
          title: msg('experiments.experimentIterationToast.stopFailed', { isMab: isMAB ? 'true' : 'false' }),
          status: 'error',
          description:
            error instanceof Error ? error.message : msg('experiments.experimentIterationToast.unknownError'),
        });
        throw error;
      }
    },
    [updateExperimentMutationAsync, projectKey, environmentKey, experiment.key, isMAB],
  );

  return useMemo(
    () => ({
      startExperimentIteration,
      stopExperimentIteration,
      isPending: isUpdateExperimentPending,
    }),
    [startExperimentIteration, stopExperimentIteration, isUpdateExperimentPending],
  );
};
